import moment from 'moment';
import { atom } from 'recoil';
import { recoilPersist } from 'recoil-persist';
import {
  GROUP_BY_EMPLOYEE_VIEW,
  GROUP_BY_OPTIONS,
  PROJECT_GROUP_BY_OPTIONS,
  SHIFTS_MODAL_SCREEN_VERSION,
  SHIFTS_MODAL_SCREEN_VERSION_LOCAL_STORAGE_KEY
} from '../../scheduling/constants';
import { localStorageRecoilEffect } from './effects';

const { persistAtom } = recoilPersist();

export const searchProjects = atom({ key: 'schedulingSearch', default: '' });

export const openShiftsExpand = atom({ key: 'schedulingOpenShiftsExpand', default: '' });

export const projectFilters = atom({ key: 'projectFilters', effects_UNSTABLE: [persistAtom] });

export const schedulingTimeRange = atom({ key: 'schedulingTimeRange' });

export const schedulingHourlyDate = atom({ key: 'schedulingHourlyDate', default: GROUP_BY_EMPLOYEE_VIEW.WEEKLY });

export const schedulingHourlyProjects = atom({ key: 'schedulingHourlyProjects', default: [] });

export const schedulingHourlyTimeOptions = atom({ key: 'schedulingHourlyTimeOptions', default: {} });

export const schedulingHourlyViewRefresh = atom({ key: 'schedulingHourlyViewRefresh', default: false });

export const schedulingMonthlyViewOption = atom({
  key: 'schedulingMonthlyViewOption',
  default: null
});

export const schedulingMonthlyViewTodayClicked = atom({
  key: 'schedulingMonthlyViewTodayClicked',
  default: false
});

export const schedulingCurrentlyAddedProjectId = atom({
  key: 'schedulingCurrentlyAddedProjectId',
  default: null
});

export const schedulingCurrentProjectHover = atom({
  key: 'schedulingCurrentProjectHover',
  default: null
});

export const schedulingCurrentMainProjectHover = atom({
  key: 'schedulingCurrentMainProjectHover',
  default: null
});

export const schedulingCurrentLinkedShiftHover = atom({
  key: 'schedulingCurrentLinkedShiftHover',
  default: null
});

export const selectedSupplierShiftIdAtom = atom({
  key: 'selectedSupplierShiftId',
  default: null
});

export const schedulingGroupBy = atom({ key: 'schedulingGroupBy', default: GROUP_BY_OPTIONS.EMPLOYEE });

export const schedulingHourlyLightView = atom({ key: 'schedulingHourlyLightView', default: false });

export const schedulingShowWarningsOnly = atom({ key: 'schedulingShowWarningsOnly', default: false });

export const schedulingStats = atom({ key: 'schedulingStats', default: false, effects_UNSTABLE: [persistAtom] });

export const sectionModal = atom({ key: 'schedulingSectionModal' });

export const copyShiftsModal = atom({ key: 'schedulingCopyShiftsModal' });

export const saveWeekAsTemplateModal = atom({ key: 'schedulingSaveWeekAsTemplateModal' });

export const loadWeeklyPlannerModal = atom({ key: 'schedulingLoadWeeklyPlannerModal' });

export const clearWeeklyPlannerModal = atom({ key: 'schedulingClearWeeklyPlannerModal' });

export const weeklyUnbookedModal = atom({ key: 'schedulingWeeklyUnbookedModal' });

export const createMainProjectAcrossBranchesModal = atom({
  key: 'schedulingCreateMainProjectAcrossBranchesModal',
  default: false
});

export const createMainProjectAcrossBranchesModalSource = atom({
  key: 'schedulingCreateMainProjectAcrossBranchesModalSource',
  default: null
});

export const ordersSummaryModal = atom({ key: 'schedulingOrdersSummaryModal' });

export const publishShiftsInRangeModal = atom({ key: 'schedulingPublishShiftsInRangeModal' });

export const deleteShiftsInRangeModal = atom({ key: 'schedulingDeleteShiftsInRangeModal' });

export const availabilityModal = atom({ key: 'schedulingAvailabilityModal', default: false });

export const selectedShiftModal = atom({ key: 'schedulingSelectedShiftModal' });

export const selectedBookingModal = atom({ key: 'schedulingSelectedBookingModal' });

export const selectedEventModal = atom({ key: 'schedulingSelectedEventModal' });

export const selectedPublishProject = atom({ key: 'schedulingSelectedPublishProject' });

export const selectedPublicationStats = atom({ key: 'schedulingSelectedPublicationStats' });

export const selectedPublicationBatchStats = atom({ key: 'schedulingSelectedPublicationBatchStats' });

export const selectedCopyEvent = atom({ key: 'schedulingSelectedCopyEvent' });

export const editBulk = atom({ key: 'schedulingEditBulkItems', default: { isActive: false, items: [] } });

export const addMainProjectMode = atom({
  key: 'schedulingAddMainProjectMode',
  default: { isActive: false, items: [] }
});

export const selectedMainProjectView = atom({ key: 'schedulingSelectedMainProjectView', default: null });

export const autoFillMode = atom({ key: 'schedulingAutoFillMode', default: false });

export const aiSchedulerModal = atom({ key: 'schedulingAiSchedulerModal', default: null });

export const aiSchedulerResults = atom({ key: 'schedulingAiSchedulerResults', default: {} });

export const highlightMissingSlots = atom({ key: 'schedulingHighlightMissingSlots', default: false });

export const selectedConfirmationShiftIds = atom({ key: 'schedulingSelectedConfirmationShiftIds' });

export const addConfirmationShiftIds = atom({ key: 'schedulingAddConfirmationShiftIds' });

export const quickProject = atom({ key: 'schedulingQuickProject', default: false });

export const newProjectOrder = atom({ key: 'schedulingProjectNewOrder', default: false });

export const orderSuccess = atom({ key: 'schedulingOrderSuccess', default: false });

export const newOrder = atom({ key: 'schedulingtNewOrder', default: false });

export const schedulingShowMap = atom({ key: 'schedulingShowMap', default: false });

export const listView = atom({ key: 'schedulingListView', default: false });

export const listViewTodayButtonClicked = atom({ key: 'listViewTodayButtonClicked', default: false });

export const availableForSlotsView = atom({ key: 'schedulingAvailableForSlotsView', default: false });

export const schedulingArchived = atom({ key: 'schedulingArchived', default: false });

export const schedulingHideWeeklyPlanners = atom({
  key: 'schedulingHideWeeklyPlanners',
  default: false,
  effects_UNSTABLE: [persistAtom]
});

export const schedulingShowMainEventColors = atom({
  key: 'schedulingShowMainEventColors',
  default: false,
  effects_UNSTABLE: [persistAtom]
});

export const schedulingShowOnlyMainEvents = atom({
  key: 'schedulingShowOnlyMainEvents',
  default: false,
  effects_UNSTABLE: [persistAtom]
});

export const showAllBranchesMode = atom({ key: 'schedulingShowAllBranches', default: false });

// `{projectId: {signature, isExpanded}}` — what the admin last saw of each event's brief.
export const schedulingAgentSeenBrief = atom({
  key: 'schedulingAgentSeenBrief',
  default: {},
  effects_UNSTABLE: [localStorageRecoilEffect('scheduling-agent-brief-seen', {})]
});

export const schedulingActiveTab = atom({ key: 'activeTab', default: 'scheduling' });

export const schedulingOrderId = atom({ key: 'orderId', default: undefined });

export const schedulingHourlyCurrentDay = atom({ key: 'currentDay', default: moment().day() + 1 });

export const publishProjectsInRange = atom({ key: 'schedulingPublishProjectsInRange', default: false });

export const shiftsModalScreenVersion = atom({
  key: 'shiftsModalScreenVersion',
  effects_UNSTABLE: [
    (params) =>
      localStorageRecoilEffect(SHIFTS_MODAL_SCREEN_VERSION_LOCAL_STORAGE_KEY, SHIFTS_MODAL_SCREEN_VERSION.OLD)(params)
  ]
});

export const hourlyViewSortBy = atom({
  key: 'schedulingHourlyViewSortByPublishProjectsInRange',
  default: { sortBy: 'firstName', sortDirection: 'asc' }
});

export const hourlyWeeklyViewSortBy = atom({
  key: 'schedulingHourlyWeeklyViewSortByPublishProjectsInRange',
  default: { sortBy: 'firstName', sortDirection: 'asc' }
});

export const timeAvailabilityByEmployeeTimeRange = atom({
  key: 'timeAvailabilityByEmployeeTimeRange',
  default: 'weekly'
});

export const selectedAvailabilityPerDayModal = atom({ key: 'schedulingSelectedAvailabilityPerDayModal' });

export const selectedApplicationHistoryModal = atom({ key: 'schedulingSelectedApplicationHistoryModal' });

export const schedulingProjectGroupBy = atom({
  key: 'schedulingProjectGroupBy',
  default: PROJECT_GROUP_BY_OPTIONS.DATE,
  // change to effects once we update recoil package
  effects_UNSTABLE: [persistAtom]
});

export const schedulingListViewSortBy = atom({
  key: 'schedulingListViewSortBy',
  default: { sortBy: 'startTime', sortDirection: 'asc' }
});

export const schedulingIndexShifts = atom({
  key: 'schedulingIndexShifts',
  default: []
});

export const schedulingIndexGroupsFilters = atom({
  key: 'schedulingIndexGroupsFilters',
  default: { filters: {}, groups: {} }
});

export const schedulingGroupRef = atom({
  key: 'schedulingGroupRef',
  default: null
});

export const schedulingProjectLiveViewInfo = atom({
  key: 'schedulingProjectLiveViewInfo',
  default: null
});

export const schedulingProjectLiveViewGroupBy = atom({
  key: 'schedulingProjectLiveViewGroupBy',
  default: PROJECT_GROUP_BY_OPTIONS.DATE
});

export const schedulingSelectedInnerProjectDate = atom({
  key: 'schedulingSelectedInnerProjectDate',
  default: null
});

export const schedulingSelectedAddChangedShiftsModal = atom({
  key: 'schedulingSelectedAddChangedShiftsModal',
  default: null
});

export const schedulingShiftFilters = atom({
  key: 'schedulingShiftFilters',
  default: {},
  effects_UNSTABLE: [persistAtom]
});

export const schedulingEmployeeFilters = atom({
  key: 'schedulingEmployeeFilters',
  default: {},
  effects_UNSTABLE: [persistAtom]
});
